import React, { useEffect, useState } from 'react'
import { ENDPOINTS } from 'src/api/endpoints'
import { convertToMin, pluralize } from 'src/lib/helpers'

import type {
  EmissionHotelSegment,
  EmissionPropertySegment,
  EmissionsInKg,
} from '@/api/co2/types'
import { ReactComponent as BathroomIcon } from '@/assets/svgs/bathtub.svg'
import { ReactComponent as BedroomIcon } from '@/assets/svgs/bedroom.svg'
import { ReactComponent as CarIcon } from '@/assets/svgs/car.svg'
import { ReactComponent as PedestrianIcon } from '@/assets/svgs/pedestrian.svg'
import { ReactComponent as SizeIcon } from '@/assets/svgs/size.svg'
import { ReactComponent as TrainIcon } from '@/assets/svgs/train.svg'
import { PropertyMatch } from '@/pages/shortlist/components/PropertyMatch'
import { EcoStatsTooltip } from '@/UIkit/EcoStatsTooltip'

import { ApprovalToggle } from './components/ApprovalToggle'
import { CornerPropertyType } from './components/CornerPropertyType'
import { InfoList } from './components/InfoList'
import { PriceSummary } from './components/PriceSummary'
import { PropertyImageCarousel } from './components/PropertyImageCarousel'
import * as S from './styles'
import { usePropertyMatch } from './usePropertyMatch'

import { get } from '../../../api/api'
import { IconGenerator } from '../../uikit'

import type { Distance, Hor, SearchProperty } from '../../../api/types/hor'

interface ShortlistPropertyProps {
  property: SearchProperty
  hor: Hor
  isApproved: boolean
  onApprovalToggle: (propertyId: number | string) => void
  isMutationOnFlight?: boolean
  areControlsDisabled?: boolean
  co2Data?: (EmissionHotelSegment | EmissionPropertySegment) & EmissionsInKg
}

export const ShortlistProperty = ({
  property,
  hor,
  isApproved,
  onApprovalToggle,
  isMutationOnFlight = false,
  areControlsDisabled = false,
  co2Data,
}: ShortlistPropertyProps) => {
  const [distances, setDistances] = useState<Distance | null>(null)

  const { propertyMatch } = usePropertyMatch(hor, property.id)

  useEffect(() => {
    get(
      `${process.env.API_BASE_URL}${ENDPOINTS.GET_LOCATION_DISTANCES(
        property?.coordinates?.longitude,
        property?.coordinates?.latitude,
        hor?.preferredLocation?.lat,
        hor?.preferredLocation?.lon
      )}`
    ).then(setDistances)
  }, [])

  const Toggle = () => (
    <ApprovalToggle
      isApproved={isApproved}
      isLoading={isMutationOnFlight}
      disabled={areControlsDisabled}
      onApprovalToggle={() => onApprovalToggle(property.id)}
    />
  )

  const policies = {
    'Minimum Stay': property.customFields['minimum-stay'] || '',
    'Free Cancellation Prior to Arrival':
      (property.customFields['cancellation-policy'] === 'Custom Policy'
        ? property.customCancellationPolicy
        : property.customFields['cancellation-policy']) || '',
    'In-House Cancellation':
      property.customFields['custom-cancellation-policy'] ||
      property.customEDNPolicy ||
      '',
    'Month to Month Lease': property.customFields['mtm-lease'] || '',
  }

  const hasPolicies = Object.values(policies).filter((v) => v).length > 0

  const amenities = {
    Parking: property.customFields.parking || '',
    Cleaning: property.customFields.cleaning || '',
    'Laundry Facilities': property.laundryFacilities || '',
    'Kitchen Type': property.kitchenType || '',
    'Air Conditioning': property.airConditioning || '',
    Reception: property.reception || '',
    'Local Registration': property.registration || '',
    View: property.view || '',
    Floor: property.floor || '',
    Utilities: property.customFields.utilities || '',
    'Pet-Friendly': property.customFields['pet-friendly'] || '',
  }

  const hasAmenities = Object.values(amenities).filter((v) => v).length > 0

  const propertyComments = property.customFields.comments || ''

  return (
    <S.PropertyCard>
      <Toggle />
      <PropertyMatch propertyMatch={propertyMatch} />

      <S.ImageSection>
        <CornerPropertyType isHotel={!!property.hotelId} />
        <PropertyImageCarousel
          images={
            property.gallery && property.gallery.length > 0
              ? property.gallery
              : [property.thumbnail]
          }
          alt={property.title}
        />
      </S.ImageSection>

      <S.PropertyDetails>
        <div>
          <S.PropertyTitle>{property.title}</S.PropertyTitle>
          <S.PropertyAddress>{property.address}</S.PropertyAddress>

          <S.PropertySpecs>
            <S.Spec>
              <IconGenerator iconComponent={BedroomIcon} size={18} />
              <span>
                {property.bedrooms} {pluralize(property.bedrooms, 'Bedroom')}
              </span>
            </S.Spec>
            <S.Spec>
              <IconGenerator iconComponent={BathroomIcon} size={18} />
              <span>
                {property.bathrooms} {pluralize(property.bathrooms, 'Bathroom')}
              </span>
            </S.Spec>
            {property.size && (
              <S.Spec>
                <IconGenerator iconComponent={SizeIcon} size={18} />
                <span>
                  {property.size.valueInSqm} sqm / {property.size.valueInSqft}{' '}
                  sqft
                </span>
              </S.Spec>
            )}
          </S.PropertySpecs>
        </div>

        {!!co2Data && (
          <EcoStatsTooltip co2Kgs={co2Data.emissionsInKg} place="bottom" />
        )}

        <PriceSummary
          totalPrice={property.totalPrice}
          currencySymbol={hor.currency?.symbol}
          priceOnHold={property.priceOnHold}
          unitOnHold={property.unitOnHold}
        />

        <S.SectionDivider />

        <S.TimeToLocationSection>
          <S.SectionTitle>Time to preferred location</S.SectionTitle>
          <S.CommuteOptions>
            <S.CommuteOption>
              <IconGenerator iconComponent={CarIcon} size={14} />
              <span>{convertToMin(distances?.drivingDuration || 0)}</span>
            </S.CommuteOption>
            <S.CommuteOption>
              <IconGenerator iconComponent={PedestrianIcon} size={14} />
              <span>{convertToMin(distances?.walkingDuration || 0)}</span>
            </S.CommuteOption>
            <S.CommuteOption>
              <IconGenerator iconComponent={TrainIcon} size={14} />
              <span>{convertToMin(distances?.busDuration || 0)}</span>
            </S.CommuteOption>
          </S.CommuteOptions>
          {property.distanceToPreferredLocation && (
            <S.DistanceText>
              Distance{' '}
              {(property.distanceToPreferredLocation / 1000).toFixed(1)} km /{' '}
              {(property.distanceToPreferredLocation / 1609.34).toFixed(1)} mi
            </S.DistanceText>
          )}
        </S.TimeToLocationSection>

        {hasPolicies && (
          <>
            <S.SectionDivider />
            <InfoList title="Policies" dictionary={policies} />
          </>
        )}

        {hasAmenities && (
          <>
            <S.SectionDivider />
            <InfoList title="Amenities" dictionary={amenities} />
          </>
        )}

        {propertyComments && (
          <>
            <S.SectionDivider />
            <div>
              <S.SectionTitle>Property Comments</S.SectionTitle>
              <S.CommentText>{propertyComments}</S.CommentText>
            </div>
          </>
        )}
      </S.PropertyDetails>
      <Toggle />
    </S.PropertyCard>
  )
}
